import React, { SyntheticEvent, useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  ListGroup,
  Row,
} from "react-bootstrap";
import FileIcon from "../common/FileIcon";
import "./DataFilesCrumbs.css";
import { Download, PlusCircleFill } from "react-bootstrap-icons";
import {
  DataFileEntry,
  isViewable,
  sortDataFileEntries,
} from "../common/fileUtils";

interface DataFilesCrumbsProps {
  rootName?: string;
  files: DataFileEntry[];
  disabled: boolean;
  onViewNewTab: (fullPath: string) => void;
  onView: (fullPath: string) => void;
  onDownload: (fullPath: string) => void;
  onDelete: (fullPath: string) => void;
  onAdd: (fullPath: string) => void;
}

/**
 * Are not only the bread crumbs! it is also the entire list of datafiles!
 */
function DataFilesCrumbs({
  rootName = "root",
  files,
  disabled,
  onView,
  onViewNewTab,
  onDownload,
  onDelete,
  onAdd,
}: DataFilesCrumbsProps) {
  const [currentEntry, setCurrentEntry] = useState<string[]>([]);
  const [selectedDataFileEntry, setSelectedDataFileEntry] =
    useState<DataFileEntry | null>(null);
  const sortDataFileEntriesRef = React.useRef<HTMLDivElement>(null);

  enum NavigationDirection {
    Next,
    Prev,
  }

  useEffect(() => {
    if (sortDataFileEntriesRef.current === null) {
      return;
    }
    sortDataFileEntriesRef.current.onkeydown = (keyboardEvent) => {
      var direction: NavigationDirection | null = null;
      switch (keyboardEvent.key) {
        case "ArrowDown":
        case "ArrowRight":
          direction = NavigationDirection.Next;
          break;
        case "ArrowUp":
        case "ArrowLeft":
          direction = NavigationDirection.Prev;
          break;
      }
      if (direction === null) {
        return;
      }
      if (keyboardEvent.target instanceof Element) {
        const currentTabIndextStr =
          keyboardEvent.target.getAttribute("tabIndex");
        const currentTabIndex =
          currentTabIndextStr !== null
            ? Number.parseInt(currentTabIndextStr)
            : null;
        if (currentTabIndex !== null) {
          const nextTabIndex =
            sortDataFileEntriesRef.current?.querySelector<HTMLDivElement>(
              `button[tabIndex='${
                currentTabIndex +
                (direction === NavigationDirection.Next ? +1 : -1)
              }']`,
            );
          nextTabIndex?.focus();
          keyboardEvent.stopPropagation();
        }
      }
    };
  });

  const onSelect = (dataFileEntry: DataFileEntry) => {
    setSelectedDataFileEntry({ ...dataFileEntry });
    onView(dataFileEntry.fullPath);
  };

  const getCurrentEntry = (): DataFileEntry => {
    let foundCurrentEntry: DataFileEntry = {
      name: "",
      fullPath: "",
      entries: files,
      file: undefined,
    };

    for (let ii = 0; ii < currentEntry.length; ++ii) {
      const sub = foundCurrentEntry.entries.find(
        (entry) => entry.entries.length > 0 && entry.name === currentEntry[ii],
      );

      if (!sub) {
        break;
      }
      foundCurrentEntry = sub;
    }

    return foundCurrentEntry;
  };

  const select = (fullPath: string) => {
    if (fullPath === "") setCurrentEntry([]);
    else setCurrentEntry(fullPath.split("/"));
  };

  const deleteEntry = (
    e: SyntheticEvent<HTMLButtonElement, MouseEvent>,
    entry: DataFileEntry,
  ) => {
    e.stopPropagation();
    if (window.confirm("Are you sure?")) onDelete(entry.fullPath);
  };

  const download = (
    e: SyntheticEvent<HTMLButtonElement, MouseEvent>,
    entry: DataFileEntry,
  ) => {
    e.stopPropagation();
    onDownload(entry.fullPath);
  };

  const view = (
    e: SyntheticEvent<HTMLButtonElement, MouseEvent>,
    entry: DataFileEntry,
  ) => {
    e.stopPropagation();
    onViewNewTab(entry.fullPath);
  };

  // ensure currentEntry is valid
  let foundEntry = { entries: files };

  for (let ii = 0; ii < currentEntry.length; ++ii) {
    let sub = foundEntry.entries.find((e) => e.name === currentEntry[ii]);

    if (sub) {
      foundEntry = sub;
    } else {
      setCurrentEntry(currentEntry.slice(0, ii));
    }
  }

  return (
    <>
      <Card className="data-files-crumbs">
        <Card.Header>
          <Row>
            <Col>
              <CustomBreadcrumbItem
                text={rootName}
                active={currentEntry.length === 0}
                onClick={() => select("")}
              />

              {currentEntry.map((entry, index) => (
                <span key={index}>
                  {"  >  "}
                  <CustomBreadcrumbItem
                    text={entry}
                    active={index === currentEntry.length - 1}
                    onClick={() =>
                      select(currentEntry.slice(0, index + 1).join("/"))
                    }
                  />
                </span>
              ))}
            </Col>
            <Col className="col-md-auto">
              <Button
                type="button"
                size="sm"
                disabled={getCurrentEntry().entries.length === 0}
                variant="outline-primary"
                onClick={() => onDownload(currentEntry.join("/"))}
              >
                <Download
                  style={{
                    display: "inline-block",
                    verticalAlign: "-.125em",
                    fill: "currentcolor",
                  }}
                />
                Download
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => onAdd(currentEntry.join("/"))}
                disabled={disabled}
                variant="outline-primary"
              >
                <PlusCircleFill
                  style={{
                    display: "inline-block",
                    verticalAlign: "-.125em",
                    fill: "currentcolor",
                  }}
                />
                {" Add File(s)"}
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body style={{ height: "320px", overflowY: "auto" }}>
          {getCurrentEntry().entries.length === 0 && (
            <i style={{ fontSize: "smaller" }}>No files</i>
          )}
          <ListGroup ref={sortDataFileEntriesRef}>
            {sortDataFileEntries(getCurrentEntry().entries).map(
              (entry, index) => (
                <ListGroup.Item
                  action
                  tabIndex={index + 1}
                  className="sortDataFileEntry"
                  active={entry.fullPath === selectedDataFileEntry?.fullPath}
                  onFocus={() => onSelect(entry)}
                  onClick={() => onSelect(entry)}
                >
                  <Row as="div" key={entry.fullPath}>
                    <Col
                      role={entry.entries.length > 0 ? "button" : "none"}
                      onClick={() => {
                        if (entry.entries.length > 0) select(entry.fullPath);
                      }}
                    >
                      <FileIcon
                        filename={entry.name}
                        isDirectory={entry.entries.length > 0}
                        size={20}
                      />
                      <span style={{ verticalAlign: "middle" }}>
                        {entry.name}
                      </span>
                    </Col>
                    <Col className="col-md-auto">
                      <ButtonGroup size="sm">
                        {entry.entries.length === 0 &&
                          isViewable(entry.name) && (
                            <Button
                              variant="info"
                              onClick={(e) => view(e, entry)}
                            >
                              View
                            </Button>
                          )}
                        <Button
                          variant="primary"
                          onClick={(e) => download(e, entry)}
                        >
                          Download
                        </Button>
                        <Button
                          variant="danger"
                          disabled={disabled}
                          onClick={(e) => deleteEntry(e, entry)}
                        >
                          Delete
                        </Button>
                      </ButtonGroup>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ),
            )}
          </ListGroup>
        </Card.Body>
      </Card>
    </>
  );
}

interface BreadcrumbItemProps {
  text: string;
  active: boolean;
  onClick: () => void;
}

function CustomBreadcrumbItem({ text, active, onClick }: BreadcrumbItemProps) {
  return active ? (
    <span>{text}</span>
  ) : (
    <a
      href="/#"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {text}
    </a>
  );
}

export default DataFilesCrumbs;
